import React, { useState } from 'react';
import { Activity, AlertCircle, PauseCircle, PlayCircle, RotateCcw, X } from 'lucide-react';
import { EcointelLottieMark } from '../EcoIntelLoader';
import { setPropertyLifecycle } from '../../api';
import { resolveLifecycleState, BUILD_STATUS } from '../../constants/buildStatus';

/**
 * Determines which lifecycle actions are valid for a given property's current
 * buildStatus. Mirrors the server-side TRANSITIONS map in
 * backend/analytics-server/marcusIngestion.js — admin_retry / admin_reactivate
 * / admin_cancel — so the UI hides controls the server would reject anyway.
 */
function lifecycleActionsFor(buildStatus) {
  const actions = [];
  if (buildStatus === BUILD_STATUS.BUILD_FAILED) {
    actions.push({
      action: 'retry',
      label: 'Retry build',
      desc: 'Re-queue this property so RB picks it up on the next discovery poll. Sets status back to intake_submitted.',
      tone: 'emerald',
      icon: RotateCcw,
    });
  }
  if (buildStatus === BUILD_STATUS.BUILD_PAUSED_CANCELLATION) {
    actions.push({
      action: 'reactivate',
      label: 'Reactivate',
      desc: 'Resume processing for a previously-paused property. Sets status back to intake_submitted so RB re-discovers it.',
      tone: 'emerald',
      icon: PlayCircle,
    });
  }
  if ([
    BUILD_STATUS.INTAKE_SUBMITTED,
    BUILD_STATUS.IN_BUILD,
    BUILD_STATUS.READY,
    BUILD_STATUS.BUILD_FAILED,
  ].includes(buildStatus)) {
    actions.push({
      action: 'cancel',
      label: 'Pause build',
      desc: 'Defensive operator action. Pauses the property so RB stops processing it. Useful while debugging an upstream regression. Reactivate later to resume.',
      tone: 'amber',
      icon: PauseCircle,
    });
  }
  return actions;
}

export default function LifecycleModal({ farm, onClose, onApplied, formatError }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(null);
  const [error, setError] = useState(null);

  if (!farm) return null;
  const lifecycle = resolveLifecycleState(farm);
  const buildStatus = farm.buildStatus || lifecycle.key;
  const actions = lifecycleActionsFor(buildStatus);

  const handleAction = async (action) => {
    setError(null);
    setSubmitting(action);
    try {
      const resp = await setPropertyLifecycle(farm.id, action, reason || undefined);
      onApplied(resp);
      onClose();
    } catch (e) {
      const status = e?.status;
      if (status === 409) {
        setError(e.message || 'This action is not valid for the property\u2019s current state.');
      } else if (status === 403) {
        setError(e.message || 'You don\u2019t have admin permission to do this.');
      } else if (status === 404) {
        setError(e.message || 'Property not found.');
      } else {
        setError(formatError ? formatError(e) : (e.message || 'Lifecycle action failed.'));
      }
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div>
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <Activity size={18} className="text-slate-500" />
              Property lifecycle
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              <strong className="text-slate-700">{farm.farmName || farm.name || farm.id}</strong>
              {' \u2014 current state: '}
              <span className="font-mono text-[11px] text-slate-700">{buildStatus || 'unknown'}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error ? (
            <div className="flex items-start gap-2 p-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-800 text-xs">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          ) : null}

          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
              Reason (optional, recorded in audit log)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 200))}
              rows={2}
              maxLength={200}
              placeholder="e.g. RB hotfix deployed; safe to retry"
              className="w-full border border-slate-200 rounded-lg p-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              disabled={!!submitting}
            />
            <div className="text-[10px] text-slate-400 text-right mt-0.5">{reason.length}/200</div>
          </div>

          {actions.length === 0 ? (
            <div className="text-xs text-slate-500 italic p-3 rounded-lg bg-slate-50 border border-slate-200">
              No lifecycle actions are available for this state. (Properties in
              <code className="font-mono mx-1">{buildStatus}</code>
              are healthy and can\u2019t be acted on directly.)
            </div>
          ) : (
            <div className="space-y-2">
              {actions.map((a) => {
                const Icon = a.icon;
                const isSubmitting = submitting === a.action;
                const toneClasses = a.tone === 'amber'
                  ? 'border-amber-200 hover:border-amber-300 hover:bg-amber-50 text-amber-800'
                  : 'border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50 text-emerald-800';
                return (
                  <button
                    key={a.action}
                    type="button"
                    onClick={() => handleAction(a.action)}
                    disabled={!!submitting}
                    className={`w-full text-left flex items-start gap-3 p-3 rounded-lg border bg-white ${toneClasses} disabled:opacity-50 disabled:cursor-wait transition-colors`}
                  >
                    <span className="shrink-0 mt-0.5">
                      {isSubmitting ? <EcointelLottieMark size={16} /> : <Icon size={16} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-sm">{a.label}</span>
                      <span className="block text-[11px] text-slate-600 mt-0.5">{a.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end p-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            disabled={!!submitting}
            className="text-xs text-slate-500 hover:text-slate-800 px-3 py-1.5 rounded-lg disabled:opacity-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
